/**
 * Find a Store — store locator (Leaflet + markercluster + OpenStreetMap, no token).
 * Data is injected as JSON by find-a-store.liquid; waits for `window.L` before init.
 */

import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const BATCH = 20;
const SEARCH_MIN = 3;
const LOADER_MIN_MS = 700; // keep the loader visible at least this long

// Zones géographiques du réglage « Boutiques affichées » (les boutiques n'ont pas de champ pays).
// Contour simplifié [lat, lng] de la France métropolitaine, élargi côté mer pour les adresses côtières.
const FRANCE = [
  [51.35,2.45], [51.05,2.6], [50.8,2.9], [50.7,3.25], [50.5,3.7], [50.33,4.1], [50.15,4.85], [49.8,4.85],
  [49.55,5.8], [49.45,6.4], [49.15,6.85], [49.1,7.4], [49.05,8.2], [48.95,8.25], [48.6,7.85], [48.1,7.6],
  [47.6,7.6], [47.5,7.1], [47.35,6.95], [46.95,6.5], [46.45,6.12], [46.27,6.1], [46.13,5.95], [46.14,6.2],
  [46.3,6.32], [46.45,6.6], [46.4,6.8], [46.1,7.05], [45.85,6.9], [45.65,7.0], [45.1,7.15], [44.7,6.95],
  [44.4,6.95], [44.1,7.7], [43.75,7.55], [43.3,7.6], [42.9,6.1], [43.1,4.7], [43.2,3.6], [42.9,3.3],
  [42.4,3.25], [42.4,2.7], [42.4,1.9], [42.55,1.7], [42.55,1.4], [42.65,0.7], [42.75,-0.1], [42.95,-0.7],
  [43.1,-1.3], [43.25,-1.4], [43.3,-1.75], [43.45,-1.85], [44.7,-1.5], [45.7,-1.5], [46.2,-1.7], [46.7,-2.4],
  [47.2,-2.7], [47.5,-3.2], [47.7,-4.4], [48.0,-4.9], [48.4,-5.3], [48.8,-4.5], [48.85,-3.0], [48.75,-2.0],
  [48.7,-1.7], [49.75,-2.1], [49.8,-1.2], [49.5,-0.2], [49.75,0.1], [50.1,1.3], [50.95,1.5],
];
const CORSE = [
  [43.1,9.2], [43.1,9.6], [42.4,9.7], [41.3,9.4], [41.3,8.5], [42.3,8.4], [43.0,9.0],
];
// Outre-mer : [latMin, latMax, lngMin, lngMax]
const OUTRE_MER = [
  [15.8, 16.6, -61.9, -60.9], // Guadeloupe
  [14.3, 14.95, -61.3, -60.75], // Martinique
  [2.0, 5.9, -54.7, -51.5], // Guyane
  [-21.45, -20.85, 55.2, 55.9], // La Réunion
  [-13.1, -12.6, 44.95, 45.35], // Mayotte
  [17.85, 18.15, -63.2, -62.75], // Saint-Martin, Saint-Barthélemy
  [46.7, 47.2, -56.5, -56.1], // Saint-Pierre-et-Miquelon
  [-28.0, -7.0, -155.0, -134.0], // Polynésie française
  [-23.0, -19.0, 163.0, 169.0], // Nouvelle-Calédonie
];

const inPolygon = (lat, lng, poly) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [yi, xi] = poly[i];
    const [yj, xj] = poly[j];
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

const inScope = (lat, lng, scope) => {
  if (scope === 'all') return true;
  if (inPolygon(lat, lng, FRANCE) || inPolygon(lat, lng, CORSE)) return true;
  return scope === 'france_dom' && OUTRE_MER.some(([a, b, c, d]) => lat >= a && lat <= b && lng >= c && lng <= d);
};

const ensureCss = (href) => {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
};

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded) return resolve();
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.addEventListener('load', () => {
      script.dataset.loaded = '1';
      resolve();
    });
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });

class FindAStore extends Component {
  requiredRefs = ['map', 'list', 'count', 'loadMore', 'search', 'results', 'empty'];

  connectedCallback() {
    super.connectedCallback();

    this.config = this.#parseJSON('[data-fas-config]') || {};
    this.labels = this.config.labels || {};
    this.logic = this.config.logic === 'and' ? 'and' : 'or';

    this.sectionId = this.dataset.sectionId || '';
    this.totalPages = Number(this.querySelector('[data-fas-pagination]')?.dataset.pages || 1);

    this.stores = (this.#parseJSON('[data-fas-stores]') || [])
      .map((s) => this.#normalize(s))
      .filter(Boolean);
    this.services = this.#parseJSON('[data-fas-services]') || [];

    this.state = { types: new Set(), services: new Set(), count: BATCH };
    this.referencePoint = null;
    this.filtered = this.stores;
    this.visible = [];
    this.markersById = {};
    this.detailCache = {};
    this.userInteracted = false;
    // La liste ne se limite à la zone visible qu'après un déplacement volontaire de la carte.
    this.listFollowsMap = false;
    this._loaderShownAt = Date.now(); // loader is rendered visible from the start

    this.recomputeList = debounce(() => this.#recomputeList(), 200);
    this.runGeocode = debounce((q) => this.#geocode(q), 250);

    this.#renderServiceFilters();
    this.#ensureServiceFilters();
    this.#bindUI();
    this.addEventListener('click', this.#onStoreLinkClick);
    this.#loadLeaflet().then((L) => {
      if (!L) {
        this.#hideLoader();
        return;
      }
      this.#initMap();
      this.#loadRemainingPages();
    });
  }

  #hideLoader() {
    const el = this.refs.loader;
    if (!el) return;
    const wait = Math.max(0, LOADER_MIN_MS - (Date.now() - (this._loaderShownAt || 0)));
    clearTimeout(this._loaderTimer);
    this._loaderTimer = setTimeout(() => el.classList.add('is-done'), wait);
  }

  #showLoader() {
    clearTimeout(this._loaderTimer);
    this._loaderShownAt = Date.now();
    this.refs.loader?.classList.remove('is-done');
  }

  /** Hide the loader once the map has finished moving (with a safety timeout). */
  #hideLoaderAfterMove() {
    if (!this.map) return this.#hideLoader();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      this.#hideLoader();
    };
    this.map.once('moveend', finish);
    setTimeout(finish, 1200);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('page:transition', this.#onPageTransition);
    // Libère le contexte WebGL de la carte vectorielle (navigation sans rechargement).
    this.map?.remove();
    this.map = null;
  }

  /* ── Setup ──────────────────────────────────────────────────────────── */

  #parseJSON(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[find-a-store] invalid JSON in', selector, e);
      return null;
    }
  }

  /** Validate + coerce a raw store entry. Returns null for unusable coords. */
  #normalize(s) {
    const lat = Number(s.lat);
    const lng = Number(s.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat === 0 && lng === 0) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    if (!inScope(lat, lng, this.config.scope || 'france')) return null;
    return {
      id: s.id,
      titre: s.titre || '',
      lat,
      lng,
      type: s.type || '',
      services: Array.isArray(s.services) ? s.services : [],
      serviceLabels: s.service_labels && typeof s.service_labels === 'object' ? s.service_labels : {},
      adresse: (s.adresse || '').replace(/\r\n?/g, '\n').trim(),
      tel: s.tel || '',
      url: s.url || '',
      reservation: s.reservation || '',
      image: s.image || '',
      _d: null,
    };
  }

  /** Load Leaflet + markercluster into <head> once (re-running them on soft page transitions breaks clustering). */
  async #loadLeaflet() {
    if (window.L && window.L.markerClusterGroup) return window.L;

    ensureCss('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');
    ensureCss('https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css');
    ensureCss('https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css');

    try {
      await loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js');
      await loadScript('https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js');
      return window.L && window.L.markerClusterGroup ? window.L : null;
    } catch (e) {
      return null;
    }
  }

  #initMap() {
    const L = window.L;
    if (!L) return;
    this.L = L;

    const center = this.config.center || [46.6, 2.4]; // France
    const zoom = this.config.zoom || 6;

    this.map = L.map(this.refs.map, {
      center,
      zoom,
      scrollWheelZoom: true,
      zoomControl: true,
      // Pas de bandes grises au-dessus des pôles : on ne peut pas faire glisser la carte au-delà.
      maxBounds: [
        [-85.05, -540],
        [85.05, 540],
      ],
      maxBoundsViscosity: 1,
    });
    this.#fitMinZoom();

    const osmAttr = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
    if (this.config.tilesKey) {
      // Thunderforest Atlas (requires a free API key from thunderforest.com)
      L.tileLayer(`https://{s}.tile.thunderforest.com/atlas/{z}/{x}/{y}.png?apikey=${this.config.tilesKey}`, {
        maxZoom: 22,
        attribution: `&copy; <a href="https://www.thunderforest.com/">Thunderforest</a>, ${osmAttr} contributors`,
      }).addTo(this.map);
    } else {
      // Keyless presets (selected via the section setting). CARTO exige désormais une clé : retiré.
      const STYLES = {
        positron: {
          vector: 'https://tiles.openfreemap.org/styles/positron',
          attribution: `<a href="https://openfreemap.org">OpenFreeMap</a> &copy; <a href="https://www.openmaptiles.org/">OpenMapTiles</a> ${osmAttr}`,
        },
        ign: {
          url: 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&FORMAT=image/png',
          attribution: '&copy; <a href="https://www.ign.fr/">IGN</a> — Géoplateforme',
          maxZoom: 19,
          muted: true,
        },
        hot: {
          url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
          attribution: `${osmAttr}, style <a href="https://www.hotosm.org/">Humanitarian OSM Team</a>, hébergé par <a href="https://openstreetmap.fr/">OpenStreetMap France</a>`,
          maxZoom: 19,
          subdomains: 'abc',
          muted: true,
        },
        osm: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: osmAttr, maxZoom: 19, subdomains: 'abc', muted: true },
        topo: { url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)', maxZoom: 17, subdomains: 'abc' },
      };
      const addRaster = (style) => {
        // Fond désaturé : les pastilles dorées doivent rester le seul élément de couleur.
        this.map.getContainer().classList.toggle('fas__map--muted', !!style.muted);
        const layer = L.tileLayer(style.url, {
          maxZoom: style.maxZoom,
          subdomains: style.subdomains || 'abc',
          attribution: style.attribution,
        });
        // Le serveur de tuiles IGN renvoie des erreurs passagères : on retente deux fois avant d'abandonner.
        layer.on('tileerror', ({ tile }) => {
          const tries = Number(tile.dataset.retry || 0);
          if (tries >= 2) return;
          tile.dataset.retry = String(tries + 1);
          const src = tile.src.replace(/([?&])_r=\d+/, '');
          setTimeout(() => {
            tile.src = `${src}${src.includes('?') ? '&' : '?'}_r=${tries + 1}`;
          }, 300 * (tries + 1));
        });
        layer.addTo(this.map);
      };

      const style = STYLES[this.config.tilesStyle] || STYLES.hot;
      if (style.vector) {
        // Couche ajoutée en différé : markercluster exige un zoom max dès sa création.
        this.map.setMaxZoom(19);
        this.#addVectorLayer(style).catch(() => addRaster(STYLES.hot));
      } else {
        addRaster(style);
      }
    }

    this.cluster = L.markerClusterGroup({
      maxClusterRadius: 50,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: false,
      zoomToBoundsOnClick: false,
      chunkedLoading: true,
      iconCreateFunction: (cluster) => {
        const n = cluster.getChildCount();
        // Same badge as the pins; spa colour when the cluster holds only spas.
        const onlySpas = cluster.getAllChildMarkers().every((m) => m.options.storeVariant === 'spa');
        const fr = document.documentElement.lang.startsWith('fr');
        const name = fr ? `${n} établissements, zoomer sur la zone` : `${n} locations, zoom in`;
        return L.divIcon({
          html: `<div class="fas-cluster__inner"><span aria-hidden="true">${n}</span><span class="visually-hidden">${name}</span></div>`,
          className: onlySpas ? 'fas-cluster fas-cluster--spa' : 'fas-cluster',
          iconSize: [48, 48],
        });
      },
    });

    // Round pin badge — primary fill for instituts, secondary for spas.
    const pinSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="#FBFAF8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="#FBFAF8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    const makeIcon = (variant) =>
      L.divIcon({
        className: `fas-marker fas-marker--${variant}`,
        html: pinSvg,
        iconSize: [48, 48],
        iconAnchor: [24, 24],
        popupAnchor: [0, -26],
      });
    this.markerIcons = { institut: makeIcon('institut'), spa: makeIcon('spa') };

    this.map.addLayer(this.cluster);

    // Cluster click: zoom in, or spiderfy when markers share the same spot.
    this.cluster.on('clusterclick', (e) => {
      const markers = e.layer.getAllChildMarkers();
      const first = markers[0].getLatLng();
      const coincident = markers.every((m) => first.distanceTo(m.getLatLng()) < 25);
      if (coincident) {
        e.layer.spiderfy();
      } else {
        this.map.fitBounds(e.layer.getBounds().pad(0.2), { maxZoom: 17 });
      }
    });

    this.#createMarkers(this.stores);
    this.#applyFilterState(); // populates the cluster + list with all stores

    const saved = this.#readSavedView();
    if (saved) {
      // Restore the view from before we navigated away — keep the user's zoom.
      this.#followMap();
      this.map.setView([saved.lat, saved.lng], saved.zoom, { animate: false });
      this.recomputeList();
    } else {
      this.#resetView();
    }

    // Recompute the visible list on every pan/zoom, and remember the view.
    this.map.on('moveend', () => {
      this.state.count = BATCH;
      this.recomputeList();
      this.#saveView();
    });

    // Track genuine user interaction so background page-loads don't yank the view.
    // Ouvrir la fiche d'un institut (clic sur son marqueur) n'est pas un déplacement de carte ;
    // cliquer un regroupement, si : il zoome.
    this.refs.map.addEventListener('pointerdown', (e) => {
      if (e.target.closest?.('.fas-marker')) {
        this.userInteracted = true;
        return;
      }
      this.#followMap();
    });
    this.refs.map.addEventListener('wheel', () => this.#followMap(), { passive: true });
    this.refs.map.addEventListener('keydown', () => this.#followMap());

    // Container size may settle late (header height, soft transition): re-measure until stable.
    this.#scheduleResize();
    this._onResize = debounce(() => {
      if (!this.map) return;
      this.map.invalidateSize();
      this.#fitMinZoom();
    }, 150);
    window.addEventListener('resize', this._onResize);
    document.addEventListener('page:transition', this.#onPageTransition);

    // Map + first list render are ready → reveal the panel.
    this.#hideLoader();
  }

  // Carte vectorielle (MapLibre) rendue sous Leaflet ; libellés forcés en français (le style est en anglais).
  async #addVectorLayer(style) {
    ensureCss('https://unpkg.com/maplibre-gl@5.24.0/dist/maplibre-gl.css');
    const [json] = await Promise.all([
      fetch(style.vector).then((r) => {
        if (!r.ok) throw new Error(`style ${r.status}`);
        return r.json();
      }),
      loadScript('https://unpkg.com/maplibre-gl@5.24.0/dist/maplibre-gl.js').then(() =>
        loadScript('https://unpkg.com/@maplibre/maplibre-gl-leaflet@0.1.3/leaflet-maplibre-gl.js')
      ),
    ]);
    if (!this.map || !this.L.maplibreGL) throw new Error('maplibre unavailable');

    const name = ['coalesce', ['get', 'name:fr'], ['get', 'name:latin'], ['get', 'name']];
    for (const layer of json.layers) {
      if (layer.layout?.['text-field'] && JSON.stringify(layer.layout['text-field']).includes('name')) {
        layer.layout['text-field'] = name;
      }
    }
    this.L.maplibreGL({ style: json }).addTo(this.map);
    this.map.attributionControl?.addAttribution(style.attribution);
  }

  #scheduleResize() {
    const fix = () => {
      if (!this.map) return;
      this.map.invalidateSize();
      // Self-heal an empty cluster after a soft page transition (idempotent).
      if (this.stores.length && this.cluster && !this.cluster.getLayers().length) {
        this.#createMarkers(this.stores);
        this.#applyFilterState();
      }
      if (!this.userInteracted && !this.referencePoint) this.#resetView();
    };
    requestAnimationFrame(fix);
    setTimeout(fix, 120);
    setTimeout(fix, 400);
  }

  #onPageTransition = () => {
    if (this.map) this.#scheduleResize();
  };

  #createMarkers(arr) {
    const L = this.L;
    for (const s of arr) {
      if (this.markersById[s.id]) continue;
      const variant = (s.type || '').toLowerCase() === 'spa' ? 'spa' : 'institut';
      const m = L.marker([s.lat, s.lng], { icon: this.markerIcons[variant], title: s.titre, storeVariant: variant });
      m.on('click', () => this.#showDetail(s));
      this.markersById[s.id] = m;
    }
  }

  // Vue d'arrivée : celle des réglages (France par défaut), et non le cadrage de toutes les
  // boutiques — avec les spas à l'étranger, ce cadrage dézoomait sur le monde entier.
  #resetView() {
    const center = this.config.center || [46.6, 2.4];
    this.map.setView(center, this.config.zoom || 6, { animate: false });
  }

  // Zoom minimal tel que le monde (256 px × 2^zoom) couvre toute la hauteur de la carte.
  #fitMinZoom() {
    const h = this.map.getContainer().clientHeight;
    if (!h) return;
    this.map.setMinZoom(Math.max(1, Math.ceil(Math.log2(h / 256))));
  }

  #followMap() {
    this.userInteracted = true;
    this.listFollowsMap = true;
  }

  // Seule une vue choisie par l'utilisateur est mémorisée : un cadrage automatique restauré
  // passerait sinon pour un choix, et filtrerait la liste dès l'arrivée.
  #saveView() {
    if (!this.map || !this.listFollowsMap) return;
    try {
      const c = this.map.getCenter();
      sessionStorage.setItem('fas:view:v2', JSON.stringify({ lat: c.lat, lng: c.lng, zoom: this.map.getZoom() }));
    } catch (e) {
      /* sessionStorage unavailable */
    }
  }

  // Ouverture d'une fiche boutique : la vue sera rendue au prochain retour sur la carte.
  #onStoreLinkClick = (e) => {
    const link = e.target.closest?.('a[href]');
    if (!link || link.hostname !== location.hostname) return;
    const isStore = this.stores.some((s) => s.url && new URL(s.url, location.origin).pathname === link.pathname);
    if (!isStore) return;
    try {
      sessionStorage.setItem('fas:return', '1');
    } catch (err) {
      /* sessionStorage unavailable */
    }
  };

  // Vue rendue seulement au retour d'une fiche boutique ; toute autre arrivée repart de zéro.
  #readSavedView() {
    try {
      const returning = sessionStorage.getItem('fas:return') === '1';
      sessionStorage.removeItem('fas:return');
      if (!returning) {
        sessionStorage.removeItem('fas:view:v2');
        return null;
      }
      const v = JSON.parse(sessionStorage.getItem('fas:view:v2'));
      if (v && Number.isFinite(v.lat) && Number.isFinite(v.lng) && Number.isFinite(v.zoom)) return v;
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  /**
   * Fetch the remaining metaobject pages (Section Rendering API) and merge them.
   * Suit le lien « page suivante » de Shopify : `?page=N` seul est ignoré pour les metaobjects (curseur phcursor requis).
   */
  async #loadRemainingPages() {
    let next = this.querySelector('[data-fas-pagination]')?.dataset.next;
    const visited = new Set();
    while (next && this.sectionId && !visited.has(next) && visited.size < 20) {
      visited.add(next);
      try {
        const url = new URL(next, location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) break;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        next = doc.querySelector('[data-fas-pagination]')?.dataset.next;
        const raw = doc.querySelector('[data-fas-stores]')?.textContent;
        if (!raw) continue;
        const more = JSON.parse(raw)
          .map((s) => this.#normalize(s))
          .filter((s) => s && !this.markersById[s.id]);
        if (!more.length) continue;
        this.stores = this.stores.concat(more);
        this.#createMarkers(more);
        this.#applyFilterState();
      } catch (e) {
        /* skip a failed page, keep what loaded */
        break;
      }
    }
    this.#ensureServiceFilters();
  }

  /** Fallback: if the services metaobject type yielded nothing, derive from stores. */
  #ensureServiceFilters() {
    if (this.services && this.services.length) return;
    const seen = new Set();
    const derived = [];
    for (const s of this.stores) {
      for (const h of s.services) {
        if (seen.has(h)) continue;
        seen.add(h);
        // Nom traduit du service (métaobjet référencé par l'établissement), sinon handle mis en forme.
        derived.push({ handle: h, label: s.serviceLabels[h] || this.#humanize(h) });
      }
    }
    if (!derived.length) return;
    derived.sort((a, b) => a.label.localeCompare(b.label, 'fr'));
    this.services = derived;
    this.#renderServiceFilters();
  }

  #humanize(handle) {
    return String(handle)
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  #bindUI() {
    this.refs.search.addEventListener('input', (e) => this.#onSearchInput(e.target.value));
    this.refs.search.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.#hideResults();
      // Flèche bas : du champ vers la première suggestion.
      if (e.key === 'ArrowDown' && !this.refs.results.hidden) {
        const first = this.refs.results.querySelector('button');
        if (first) {
          e.preventDefault();
          first.focus();
        }
      }
    });
    // Flèches haut / bas entre les suggestions, Échap revient au champ.
    this.refs.results.addEventListener('keydown', (e) => {
      const items = [...this.refs.results.querySelectorAll('button')];
      const i = items.indexOf(document.activeElement);
      if (i === -1) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
        (next < 0 ? this.refs.search : items[Math.min(next, items.length - 1)]).focus();
      } else if (e.key === 'Escape') {
        this.#hideResults();
        this.refs.search.focus();
      }
    });
    this.refs.results.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-lat]');
      if (btn) this.#selectCity(btn.dataset);
    });
    this.refs.loadMore.addEventListener('click', () => {
      this.state.count += BATCH;
      this.#renderRows();
    });
    this.refs.list.addEventListener('click', (e) => {
      if (e.target.closest('a')) return; // book / directions / tel / name keep their own links
      const card = e.target.closest('[data-id]');
      // Navigate to the boutique page via the name link (keeps the page transition).
      card?.querySelector('a[href]')?.click();
    });
    this.refs.list.addEventListener('keydown', (e) => {
      if (e.target.closest('a')) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const card = e.target.closest('[data-id]');
      if (card) {
        e.preventDefault();
        card.querySelector('a[href]')?.click();
      }
    });

    // Back link (in the header) → return to the listing.
    if (this.refs.head) {
      this.refs.head.addEventListener('click', (e) => {
        if (e.target.closest('[data-fas-back]')) this.#backToListing();
      });
    }

    const geo = this.querySelector('[data-fas-geolocate]');
    if (geo) geo.addEventListener('click', () => this.#locate());

    const apply = this.querySelector('[data-fas-apply]');
    if (apply) apply.addEventListener('click', () => this.#applyFilters());
    const reset = this.querySelector('[data-fas-reset]');
    if (reset) reset.addEventListener('click', () => this.#resetFilters());

    // Type filter is single-select: checking one unchecks the other(s).
    const typeInputs = [...this.querySelectorAll('[data-fas-type]')];
    typeInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        typeInputs.forEach((other) => {
          if (other !== input) other.checked = false;
        });
      });
    });
  }

  #renderServiceFilters() {
    const wrap = this.querySelector('[data-fas-services-list]');
    if (!wrap) return;
    if (!this.services.length) {
      wrap.innerHTML = `<p class="tw:text-sm tw:text-muted tw:m-0">${this.#esc(this.labels.no_services || '—')}</p>`;
      return;
    }
    wrap.innerHTML = this.services
      .map(
        (s) => `<label class="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:py-2 tw:cursor-pointer">
          <span class="tw:text-base tw:text-muted">${this.#esc(s.label)}</span>
          <input type="checkbox" value="${this.#esc(s.handle)}" data-fas-service class="filter-checkbox tw:appearance-none tw:w-5 tw:h-5 tw:bg-background tw:border tw:border-border tw:rounded-[4px] tw:cursor-pointer tw:relative tw:shrink-0">
        </label>`
      )
      .join('');
  }

  /* ── Search (Géoplateforme IGN — géocodage de l'État, sans clé, autocomplétion autorisée) ── */

  #onSearchInput(value) {
    const q = value.trim();
    if (q.length < SEARCH_MIN) {
      this.#hideResults();
      return;
    }
    this.#renderResultsMessage(this.labels.searching || 'Recherche…');
    this.runGeocode(q);
  }

  async #geocode(q) {
    try {
      this.searchAbort?.abort();
      this.searchAbort = new AbortController();
      const url = `https://data.geopf.fr/geocodage/search?type=municipality&autocomplete=1&limit=5&q=${encodeURIComponent(
        q
      )}`;
      const res = await fetch(url, {
        signal: this.searchAbort.signal,
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      // GeoJSON → forme attendue par #renderResults (coordinates = [lng, lat]).
      const list = (data.features || []).map((f) => ({
        lat: f.geometry.coordinates[1],
        lon: f.geometry.coordinates[0],
        display_name: f.properties.postcode ? `${f.properties.label} (${f.properties.postcode})` : f.properties.label,
      }));
      this.#renderResults(list);
    } catch (e) {
      if (e.name === 'AbortError') return;
      this.#hideResults();
    }
  }

  #renderResults(list) {
    if (!Array.isArray(list) || !list.length) {
      this.#renderResultsMessage(this.labels.no_city || 'Aucun lieu trouvé');
      return;
    }
    this.refs.results.innerHTML = list
      .map(
        (r) => `<button type="button" class="fas__result-btn tw:block tw:w-full tw:text-left tw:px-4 tw:py-2.5 tw:text-sm tw:bg-transparent tw:border-0 tw:cursor-pointer"
          data-lat="${this.#esc(r.lat)}" data-lng="${this.#esc(r.lon)}" data-label="${this.#esc(r.display_name)}">
          ${this.#esc(r.display_name)}
        </button>`
      )
      .join('');
    this.refs.results.hidden = false;
  }

  #renderResultsMessage(msg) {
    this.refs.results.innerHTML = `<p class="tw:px-4 tw:py-2.5 tw:text-sm tw:text-muted tw:m-0">${this.#esc(
      msg
    )}</p>`;
    this.refs.results.hidden = false;
  }

  #hideResults() {
    this.refs.results.hidden = true;
    this.refs.results.innerHTML = '';
  }

  #selectCity(data) {
    this.refs.search.value = data.label || '';
    this.#hideResults();
    this.#showLoader();
    this.#setReference(Number(data.lat), Number(data.lng), 12);
    this.#hideLoaderAfterMove();
  }

  /* ── Geolocation ────────────────────────────────────────────────────── */

  #locate() {
    if (!navigator.geolocation) return this.#geoError();
    this.#showLoader();
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.#hideResults();
        this.#setReference(pos.coords.latitude, pos.coords.longitude, 12);
        this.#hideLoaderAfterMove();
      },
      () => {
        this.#hideLoader();
        this.#geoError();
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  }

  #geoError() {
    this.#renderResultsMessage(this.labels.geo_error || 'Localisation indisponible. Recherchez une ville.');
    setTimeout(() => this.#hideResults(), 3500);
  }

  #setReference(lat, lng, zoom) {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    this.referencePoint = { lat, lng };
    this.#followMap();
    if (this.map) {
      if (this.refMarker) this.map.removeLayer(this.refMarker);
      this.refMarker = this.L.circleMarker([lat, lng], {
        radius: 7,
        color: '#fff',
        weight: 2,
        fillColor: this.config.markerColor || '#c69b4a',
        fillOpacity: 1,
      }).addTo(this.map);
      this.state.count = BATCH;
      this.map.setView([lat, lng], zoom);
    }
  }

  /* ── Filters ────────────────────────────────────────────────────────── */

  #applyFilters() {
    const types = [...this.querySelectorAll('[data-fas-type]:checked')].map((i) => i.value.toLowerCase());
    const services = [...this.querySelectorAll('[data-fas-service]:checked')].map((i) => i.value);
    this.state.types = new Set(types);
    this.state.services = new Set(services);
    this.#showLoader();
    this.#applyFilterState();
    this.querySelector('dialog-component')?.closeDialog?.();
    this.#hideLoader();
  }

  #resetFilters() {
    this.querySelectorAll('[data-fas-type]:checked, [data-fas-service]:checked').forEach((i) => {
      i.checked = false;
    });
    this.state.types = new Set();
    this.state.services = new Set();
    this.#applyFilterState();
  }

  #applyFilterState() {
    const { types, services } = this.state;
    this.filtered = this.stores.filter((s) => {
      if (types.size && !types.has((s.type || '').toLowerCase())) return false;
      if (services.size) {
        if (this.logic === 'and') {
          for (const h of services) if (!s.services.includes(h)) return false;
        } else if (!s.services.some((h) => services.has(h))) {
          return false;
        }
      }
      return true;
    });

    if (this.cluster) {
      this.cluster.clearLayers();
      this.cluster.addLayers(this.filtered.map((s) => this.markersById[s.id]).filter(Boolean));
    }
    this.state.count = BATCH;
    this.#updateFilterBadge();
    this.#recomputeList();
  }

  #updateFilterBadge() {
    const badge = this.querySelector('[data-fas-filter-count]');
    if (!badge) return;
    const n = this.state.types.size + this.state.services.size;
    badge.textContent = n ? String(n) : '';
    badge.hidden = n === 0;
  }

  /* ── List (viewport + distance) ─────────────────────────────────────── */

  #recomputeList() {
    let list = this.filtered;
    if (this.map && this.listFollowsMap) {
      const b = this.map.getBounds();
      list = list.filter((s) => b.contains([s.lat, s.lng]));
    }
    if (this.referencePoint && this.L) {
      const ref = this.L.latLng(this.referencePoint.lat, this.referencePoint.lng);
      for (const s of list) s._d = ref.distanceTo([s.lat, s.lng]);
      list = list.slice().sort((a, b) => a._d - b._d);
    } else {
      list = list.slice().sort((a, b) => a.titre.localeCompare(b.titre, 'fr'));
    }
    this.visible = list;
    this.#updateCount(list);
    this.#renderRows();
  }

  #renderRows() {
    const total = this.visible.length;
    const n = Math.min(this.state.count, total);

    if (total === 0) {
      this.refs.list.innerHTML = '';
      this.refs.list.hidden = true;
      this.refs.empty.hidden = false;
      this.refs.loadMore.hidden = true;
      return;
    }

    this.refs.empty.hidden = true;
    this.refs.list.hidden = false;
    this.refs.list.innerHTML = this.visible
      .slice(0, n)
      .map((s) => this.#card(s))
      .join('');
    this.refs.loadMore.hidden = n >= total;
  }

  /* ── Detail panel ───────────────────────────────────────────────────── */

  async #showDetail(store) {
    this.#showLoader();
    // Remember the current view (only when entering from the listing) so "back" restores it.
    if (this.map && this.refs.detail.hidden) {
      this._prevView = { center: this.map.getCenter(), zoom: this.map.getZoom() };
    }
    // Zoom in and center on the clicked point (don't let auto-refit fight us).
    this.userInteracted = true;
    if (this.map) {
      const target = Math.max(this.map.getZoom() || 0, 16);
      this.map.setView([store.lat, store.lng], target, { animate: true });
    }

    const detail = await this.#fetchDetail(store);
    this.refs.detail.innerHTML = this.#detailHTML(detail || {}, store);
    // Header swaps search → back link (back link stays above the map on mobile).
    if (this.refs.headSearch) this.refs.headSearch.hidden = true;
    if (this.refs.headBack) this.refs.headBack.hidden = false;
    this.refs.listing.hidden = true;
    this.refs.detail.hidden = false;
    this.#hideLoader();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  #backToListing() {
    this.refs.detail.hidden = true;
    this.refs.detail.innerHTML = '';
    if (this.refs.headSearch) this.refs.headSearch.hidden = false;
    if (this.refs.headBack) this.refs.headBack.hidden = true;
    this.refs.listing.hidden = false;
    if (this.map && this._prevView) {
      // Restore the exact view we had before clicking, then let the list recompute.
      this.userInteracted = false;
      this.map.setView(this._prevView.center, this._prevView.zoom, { animate: true });
      this._prevView = null;
    }
  }

  async #fetchDetail(store) {
    if (!store.url) return null;
    if (this.detailCache[store.id]) return this.detailCache[store.id];
    const extract = async (url) => {
      try {
        const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) return null;
        const html = await res.text();
        const raw = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-boutique-info]')?.textContent;
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    };
    const sep = store.url.includes('?') ? '&' : '?';
    const data = (await extract(`${store.url}${sep}section_id=main`)) || (await extract(store.url));
    if (data) this.detailCache[store.id] = data;
    return data;
  }

  // Libellé selon ce qui est listé (instituts, spas ou les deux) ; {s} = pluriel à partir de 2.
  #updateCount(list) {
    const n = list.length;
    const isSpa = (s) => (s.type || '').toLowerCase() === 'spa';
    const hasSpa = list.some(isSpa);
    const hasInstitut = list.some((s) => !isSpa(s));
    let tpl = this.labels.found;
    if (hasSpa && hasInstitut) tpl = this.labels.found_mixed || tpl;
    else if (hasSpa) tpl = this.labels.found_spa || tpl;
    this.refs.count.textContent = (tpl || '{count}').replaceAll('{count}', n).replaceAll('{s}', n >= 2 ? 's' : '');
  }

  /* ── HTML builders ──────────────────────────────────────────────────── */

  #typePill(type) {
    const isSpa = String(type).toLowerCase().includes('spa');
    const tone = isSpa ? 'tw:bg-secondary' : 'tw:bg-primary';
    // Libellé traduisible (réglages de la section), la donnée reste « Institut » / « Spa ».
    const label = (isSpa ? this.labels.type_spa : this.labels.type_institut) || type;
    return `<span class="tw:inline-block tw:text-[12px] tw:leading-none tw:py-1.5 tw:px-3 tw:mb-3 tw:rounded-full tw:text-background ${tone}">${this.#esc(
      label
    )}</span>`;
  }

  #card(s) {
    const esc = (v) => this.#esc(v);
    const dist =
      this.referencePoint && Number.isFinite(s._d)
        ? `<span class="tw:text-sm tw:text-muted tw:whitespace-nowrap tw:shrink-0">${this.#distance(s._d)}</span>`
        : '';
    const image = s.image
      ? `<div class="tw:aspect-[421/316]" style="background: url('${esc(s.image)}') lightgray 50% / cover no-repeat;"></div>`
      : '';
    const surtitle = s.type ? this.#typePill(s.type) : '';
    const arrow = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="tw:shrink-0" aria-hidden="true"><path d="M9 6L15 12L9 18" stroke="#C69B4A"></path></svg>`;
    const book = s.reservation
      ? `<a href="${esc(s.reservation)}" target="_blank" rel="noopener" class="cta-link tw:text-xl"><span class="cta-link__text">${esc(
        this.labels.book || 'Prendre rendez-vous'
      )}</span></a>`
      : '';
    const dir = `<a href="https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}" target="_blank" rel="noopener" class="cta-link tw:text-xl"><span class="cta-link__text">${esc(
      this.labels.directions || 'Itinéraire'
    )}</span></a>`;
    const tel = s.tel
      ? `<a href="tel:${esc(s.tel)}" class="cta-link tw:text-xl tw:block tw:mt-2"><span class="cta-link__text">${esc(
        s.tel
      )}</span></a>`
      : '';

    return `<li>
      <div class="fas__card tw:bg-background-alt tw:rounded-xs tw:overflow-hidden tw:cursor-pointer" data-id="${esc(
      s.id
    )}">
        ${image}
        <div class="tw:p-4">
          ${surtitle}
          <div class="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:pb-4 tw:mb-4 tw:border-b tw:border-border">
            <a href="${esc(s.url)}" class="tw:font-heading tw:text-2xl tw:text-primary tw:leading-tight tw:no-underline tw:hover:underline">${esc(
      s.titre
    )}</a>
            ${arrow}
          </div>
          <div class="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:mb-4">
            <p class="tw:text-sm tw:text-muted tw:m-0 tw:leading-snug tw:whitespace-pre-line">${esc(s.adresse)}</p>
            ${dist}
          </div>
          <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-x-6 tw:gap-y-1">${book}${dir}</div>
          ${tel}
        </div>
      </div>
    </li>`;
  }

  /** "Store information" panel — same data as the single page, reordered. */
  #detailHTML(d, store) {
    const esc = (v) => this.#esc(v);
    const L = this.labels;
    const titre = d.titre || store.titre || '';
    const url = d.url || store.url || '';
    const lat = d.lat != null && d.lat !== '' ? d.lat : store.lat;
    const lng = d.lng != null && d.lng !== '' ? d.lng : store.lng;
    const tel = d.tel || store.tel || '';
    const telHref = String(tel).replace(/\s/g, '');
    const reservation = d.reservation || store.reservation || '';
    const type = d.type || store.type || '';
    const adresse = d.adresse || store.adresse || '';

    const heading = `<h2 class="tw:font-heading tw:text-2xl tw:m-0">${esc(L.detail_title || 'Informations')}</h2>`;
    const image = d.image
      ? `<img src="${esc(d.image)}" alt="${esc(titre)}" loading="lazy" class="tw:w-full tw:aspect-[421/316] tw:object-cover tw:rounded-xs tw:mt-4">`
      : '';
    const typeEl = type ? `<div class="tw:mt-6">${this.#typePill(type)}</div>` : '';
    const title = `<h3 class="tw:font-heading tw:text-2xl tw:text-primary tw:leading-tight tw:m-0 tw:mt-1">${esc(titre)}</h3>`;
    const adresseEl = adresse ? `<p class="tw:text-sm tw:text-muted tw:m-0 tw:mt-4 tw:whitespace-pre-line">${esc(adresse)}</p>` : '';
    const telEl = tel ? `<a href="tel:${esc(telHref)}" class="tw:block tw:text-sm tw:text-muted tw:no-underline tw:mt-1 tw:hover:underline">${esc(tel)}</a>` : '';

    const hoursList = Array.isArray(d.hours) ? d.hours : [];
    const hours = hoursList.some((h) => h.value)
      ? `<div class="tw:py-8 tw:border-t tw:border-border"><h2 class="tw:font-heading tw:text-2xl tw:m-0 tw:mb-4">${esc(L.hours || 'Horaires')}</h2><dl class="tw:m-0 tw:flex tw:flex-col tw:gap-2 tw:text-sm tw:text-muted">${hoursList
        .map((h) => `<div class="tw:flex tw:items-center tw:justify-between tw:gap-4"><dt>${esc(h.day)}</dt><dd class="tw:m-0">${esc(h.value || L.closed || '')}</dd></div>`)
        .join('')}</dl></div>`
      : '';

    const services = Array.isArray(d.services) && d.services.length
      ? `<div class="tw:py-8 tw:border-t tw:border-border"><h2 class="tw:font-heading tw:text-2xl tw:m-0 tw:mb-4">${esc(L.services || 'Services proposés')}</h2><ul class="tw:list-none tw:p-0 tw:m-0 tw:flex tw:flex-col tw:gap-2">${d.services
        .map((sv) => `<li class="tw:text-sm tw:text-primary">${esc(sv)}</li>`)
        .join('')}</ul></div>`
      : '';

    const insta = d.instagram ? `<a href="${esc(d.instagram)}" target="_blank" rel="noopener" aria-label="Instagram" class="tw:text-foreground tw:hover:opacity-70 tw:transition-opacity"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5"></rect><circle cx="12" cy="12" r="4"></circle><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"></circle></svg></a>` : '';
    const fb = d.facebook ? `<a href="${esc(d.facebook)}" target="_blank" rel="noopener" aria-label="Facebook" class="tw:text-foreground tw:hover:opacity-70 tw:transition-opacity"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg></a>` : '';
    const socials = insta || fb ? `<div class="tw:flex tw:items-center tw:gap-3">${insta}${fb}</div>` : '';
    const desc = d.description ? `<div class="rte tw:text-base tw:text-muted tw:leading-relaxed">${d.description}</div>` : '';
    const more = url ? `<a href="${esc(url)}" class="cta-link tw:text-sm"><span class="cta-link__text">${esc(L.more || 'En savoir plus')}</span></a>` : '';
    const about = socials || desc
      ? `<div class="tw:py-8 tw:border-t tw:border-border tw:flex tw:flex-col tw:gap-4"><h2 class="tw:font-heading tw:text-2xl tw:m-0">${esc((L.about_prefix || 'À propos de') + ' ' + titre)}</h2>${socials}${desc}${more}</div>`
      : '';

    const calIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FBFAF8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>`;
    const phoneIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 21 21" fill="none" aria-hidden="true"><path d="M20.3881 15.4201V18.4201C20.3892 18.6986 20.3322 18.9743 20.2206 19.2294C20.1091 19.4846 19.9454 19.7137 19.7402 19.902C19.535 20.0902 19.2927 20.2336 19.0289 20.3228C18.765 20.412 18.4855 20.4452 18.2081 20.4201C15.131 20.0857 12.1751 19.0342 9.57812 17.3501C7.16194 15.8148 5.11345 13.7663 3.57812 11.3501C1.88809 8.7413 0.83636 5.77109 0.508117 2.6801C0.483127 2.40356 0.515992 2.12486 0.604617 1.86172C0.693243 1.59859 0.835688 1.35679 1.02288 1.15172C1.21008 0.94665 1.43792 0.782806 1.69191 0.670619C1.94589 0.558432 2.22046 0.500358 2.49812 0.500097H5.49812C5.98342 0.495321 6.45391 0.667176 6.82188 0.98363C7.18985 1.30008 7.43019 1.73954 7.49812 2.2201C7.62474 3.18016 7.85957 4.12282 8.19812 5.0301C8.33266 5.38802 8.36178 5.77701 8.28202 6.15098C8.20227 6.52494 8.01698 6.86821 7.74812 7.1401L6.47812 8.4101C7.90167 10.9136 9.97457 12.9865 12.4781 14.4101L13.7481 13.1401C14.02 12.8712 14.3633 12.6859 14.7372 12.6062C15.1112 12.5264 15.5002 12.5556 15.8581 12.6901C16.7654 13.0286 17.7081 13.2635 18.6681 13.3901C19.1539 13.4586 19.5975 13.7033 19.9146 14.0776C20.2318 14.4519 20.4003 14.9297 20.3881 15.4201Z" stroke="#FBFAF8" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;
    const bookHref = reservation || (telHref ? `tel:${telHref}` : '');
    const book = bookHref
      ? `<a href="${esc(bookHref)}" ${reservation ? 'target="_blank" rel="noopener"' : ''} class="cta-filled cta-filled--primary tw:flex tw:items-center tw:justify-center tw:gap-4 tw:rounded-full tw:py-4 tw:px-8 tw:text-xl tw:font-medium tw:no-underline tw:w-full">${reservation ? calIcon : phoneIcon}${esc(L.book || 'Prendre un rendez-vous')}</a>`
      : '';
    const dir = lat != null && lng != null
      ? `<a href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}" target="_blank" rel="noopener" class="cta-link tw:text-xl tw:block tw:text-center tw:mt-4"><span class="cta-link__text">${esc(L.directions || 'Itinéraire')}</span></a>`
      : '';
    const cta = book || dir ? `<div class="tw:mt-8 tw:pb-8">${book}${dir}</div>` : '';

    return `${heading}${image}${typeEl}${title}${adresseEl}${telEl}${cta}${hours}${services}${about}`;
  }

  #distance(meters) {
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(2)} km`;
  }

  #esc(value) {
    return String(value ?? '').replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }
}

if (!customElements.get('find-a-store')) {
  customElements.define('find-a-store', FindAStore);
}
